import {
  FreeTextTeaserEntry,
  TeaserEntry,
  AudioTeaserEntry,
} from "../helper/interfaces";
import React, { useContext, useState } from "react";

import { AppTheme, ContentType, TeaserViewType } from "../helper/enums";
import { AppContext } from "../provider/AppContextProvider";
import TeaserInfoBox from "./TeaserInfoBox";
import TeaserQualifio from "./TeaserQualifio";
import { getTeaserImageUrl, getTeaserTypeIcons } from "../helper/images";
import TeaserCollectiveBox from "./TeaserCollectiveBox";
import TeaserAd from "./TeaserAd";
import { AdInformationInterface } from "../helper/PageInformationInterface";
import TeaserVideo from "./TeaserVideo";
import TeaserImage from "./TeaserImage";
import TeaserLinkBox from "./TeaserLinkBox";
import TeaserLiveticker from "./TeaserLiveticker";
import { useLoggedRouter } from "../helper/useLoggedRouter";
import Teaser23Grad from "./Teaser23Grad";
import TeaserFreeHtml from "./TeaserFreeHtml";
import TeaserDailyMotionVideoIFrame from "./TeaserDailyMotionVideoIFrame";
import TeaserKickerMatchday from "./TeaserKickerMatchday";
import TeaserKickerLiveTicker from "./TeaserKickerLiveTicker";
import SharingMenuNN from "./SharingMenuNN";
import TeaserBaseNB from "./TeaserBaseNB";
import TeaserBaseNN from "./TeaserBaseNN";
import { getLinkObjectFromTeaser } from "../helper/link";
import TeaserCatchline from "./TeaserCatchline";
import TeaserAmazonAffiliateBox from "./TeaserAmazonAffiliateBox";

export interface TeaserData {
  teaser: TeaserEntry;
  className?: string;
  navigate?: (id?: string, ressortId?: string) => void;
  adInformation?: AdInformationInterface;
  ignoreLead?: boolean;
  isImportantTeaser?: boolean;
  viewType?: TeaserViewType;
  isArticleTeaser?: boolean;
  imageUrl?: string;
}

function Teaser(data: TeaserData) {
  const [showSharing, setShowSharing] = useState<boolean>(false);
  const [routerPush] = useLoggedRouter();

  const internalNavigate = async (e, linkObject) => {
    e.stopPropagation();
    e.preventDefault();

    if (linkObject.articleId || linkObject.ressortId) {
      if (data.navigate) {
        data.navigate(linkObject.articleId, linkObject.ressortId);
      } else {
        await routerPush(`/article/${encodeURIComponent(data.teaser.id)}`);
      }
    }
  };

  const teaser = data.teaser;

  const image = teaser?.images ? teaser.images[0] : undefined;
  const imageUrl = image && getTeaserImageUrl(image);

  const teaserClassName = `teaser teaser--type-${teaser.type} ${
    imageUrl ? "teaser--image" : ""
  } ${teaser.featured ? "teaser--featured" : ""} ${
    teaser.highlight ? "teaser--highlight" : ""
  } ${data.className ? data.className : ""}`;

  const { audioPlayer, setAudioPlayer } = useContext(AppContext);

  const isNNTheme = process.env.NEXT_PUBLIC_APP_THEME == AppTheme.nn;

  switch (teaser.type) {
    case ContentType.CollectiveBox:
      return <TeaserCollectiveBox teaser={data.teaser} />;

    case ContentType.Podcast:
      return (
        <React.Fragment>
          <button
            onClick={() => {
              setAudioPlayer({
                topline: teaser.topline,
                title: teaser.title,
                playing: !(
                  audioPlayer?.playing &&
                  audioPlayer.audioUrl === (teaser as AudioTeaserEntry).audioUrl
                ),
                audioUrl: (teaser as AudioTeaserEntry).audioUrl,
              });
            }}
            data-name={`article-${teaser.id}`}
            className={teaserClassName}
          >
            {!isNNTheme && <TeaserBaseNB teaser={teaser} ignoreLead={data.ignoreLead} />}
            {isNNTheme && <TeaserBaseNN teaser={teaser} viewType={data.viewType} isImportantTeaser={data.isImportantTeaser} ignoreLead={data.ignoreLead} isArticleTeaser={data.isArticleTeaser}/>}
          </button>
        </React.Fragment>
      );

    case ContentType.AmazonAffiliateBox:
      return <TeaserAmazonAffiliateBox teaser={data.teaser} navigate={data.navigate} />;
    case ContentType.InfoBox:
      return <TeaserInfoBox teaser={data.teaser} />;
    case ContentType.FreeHtml:
      return <TeaserFreeHtml teaser={data.teaser} />;
    case ContentType.InfoBoxTeaser:
      return <TeaserInfoBox teaser={data.teaser} />;
    case ContentType.Linkbox:
      return <TeaserLinkBox teaser={data.teaser} navigate={data.navigate} />;
    case ContentType.Advert:
      return (
        <TeaserAd teaser={data.teaser} adInformation={data.adInformation} />
      );
    case ContentType.Video:
    case ContentType.GlomexVideo:
      return <TeaserVideo teaser={data.teaser} />;
    case ContentType.TwentyThreeDegrees:
      return <Teaser23Grad teaser={data.teaser} />;
    case ContentType.DailyMotionVideo:
      return <TeaserDailyMotionVideoIFrame teaser={data.teaser} />;
    case ContentType.Image:
      return <TeaserImage teaser={data.teaser} />;
    case ContentType.QualifioBox:
      return <TeaserQualifio teaser={data.teaser} />;
    case ContentType.Liveticker:
      return <TeaserLiveticker teaser={data.teaser} />;
    case ContentType.KickerMatchday:
      return <TeaserKickerMatchday teaser={data.teaser} />;
    case ContentType.KickerLiveTicker:
      return <TeaserKickerLiveTicker teaser={data.teaser} />;
    case ContentType.Catchline:
      return <TeaserCatchline teaser={data.teaser} />;

    default:
      break;
  }

  const linkObject = getLinkObjectFromTeaser(teaser);
  const webViewUrl = linkObject.externalLink;
  const sharingUrl = linkObject.externalLink;

  return (
    <>
      {showSharing && (
        <SharingMenuNN
          teaser={teaser}
          sharingUrl={sharingUrl ?? ''}
          onClose={() => setShowSharing(false)}
        />
      )}

      {/* {!webViewUrl && teaser.seoUrl?.id && (
        <div>
          <a
            href={`/${linkObject.articleId ? "article" : "ressort"}/${
              teaser.seoUrl?.id
            }`}
            key={`teaser-link-${teaser.id}`}
            onClick={(e) => {
              internalNavigate(e, linkObject);
            }}
            data-name={`article-${teaser.id}`}
            className={teaserClassName}
          >
            {!isNNTheme && <TeaserBaseNB teaser={teaser} ignoreLead={data.ignoreLead} />}
            {isNNTheme && <TeaserBaseNN teaser={teaser} viewType={data.viewType} isImportantTeaser={data.isImportantTeaser} ignoreLead={data.ignoreLead} isArticleTeaser={data.isArticleTeaser}/>}
          </a>
        </div>
      )} */}
      {!webViewUrl && (
        <div>
          <a
            href={linkObject.internalLink}
            key={`teaser-link-${teaser.id}`}
            onClick={(e) => {
              internalNavigate(e, linkObject);
            }}
            data-name={`article-${teaser.id}`}
            className={teaserClassName}
          >
            {!isNNTheme && <TeaserBaseNB teaser={teaser} ignoreLead={data.ignoreLead} />}
            {isNNTheme && <TeaserBaseNN teaser={teaser} viewType={data.viewType} isImportantTeaser={data.isImportantTeaser} ignoreLead={data.ignoreLead} isArticleTeaser={data.isArticleTeaser}/>}
          </a>
        </div>
      )}
      {webViewUrl && (
        <div>
          <a
            href={webViewUrl}
            key={`teaser-link-${teaser.id}`}
            target="_blank"
            rel="noreferrer"
            data-name={`article-${teaser.id}`}
            className={teaserClassName}
          >
            {!isNNTheme && <TeaserBaseNB teaser={teaser} ignoreLead={data.ignoreLead} />}
            {isNNTheme && <TeaserBaseNN teaser={teaser} viewType={data.viewType} isImportantTeaser={data.isImportantTeaser} ignoreLead={data.ignoreLead} isArticleTeaser={data.isArticleTeaser}/>}
          </a>
        </div>
      )}
      {(teaser as FreeTextTeaserEntry).linkButtonUrl &&
        (teaser as FreeTextTeaserEntry).linkButtonText && (
          <div className="teaser-button">
            <a
              data-name={`article-${teaser.id}`}
              className="teaser-button__btn btn btn-primary-dark"
              href={(teaser as FreeTextTeaserEntry).linkButtonUrl}
              target="_blank"
              rel="noreferrer"
            >
              {(teaser as FreeTextTeaserEntry).linkButtonText}
            </a>
          </div>
        )}
    </>
  );
}

export default Teaser;
